import React, { useCallback, useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { ethers } from 'ethers';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Link from 'next/link';
import Countdown from 'react-countdown';

import useRaffles from '../hooks/useRaffles';
import useRainbowBalance from '../hooks/useRainbowBalance';
import useRainbows from '../hooks/useRainbows';
import { formatEther } from '../util';
import RainbowBalance from './RainbowBalance';
import useRaffleParticipants from '../hooks/useRaffleParticipants';

const raffleAccount = process.env.NEXT_PUBLIC_RAFFLE_ACCOUNT;

const RaffleEntryForm = ({ raffleKey }) => {
  const raffle = useRaffles(raffleKey);

  const { account, library } = useWeb3React();
  const rainbows = useRainbows();
  const rainbowBalance = useRainbowBalance();

  const [ticketPrice] = useState(
    ethers.constants.WeiPerEther.mul(raffle.ticket_price)
  );
  const [ticketCount, setTicketCount] = useState(1);
  const [buying, setBuying] = useState(false);

  const { participants } = useRaffleParticipants(raffleKey);

  const [raffleStarted, setRaffleStarted] = useState(false);
  const [raffleEnded, setRaffleEnded] = useState(false);

  const [raffleStart, setRaffleStart] = useState(new Date(0));
  const [raffleEnd, setRaffleEnd] = useState(new Date());

  useEffect(() => {
    setRaffleStart(new Date(raffle.start_time));
    setRaffleEnd(new Date(raffle.end_time));
  }, [raffle]);

  useEffect(() => {
    const updateRaffleState = () => {
      setRaffleStarted(raffleStart.getTime() < Date.now());
      setRaffleEnded(raffleEnd.getTime() < Date.now());
    };
    updateRaffleState();
    const intervalId = setInterval(updateRaffleState, 1000);
    return () => clearInterval(intervalId);
  }, [raffleStart, raffleEnd]);

  const updateTicketCount = useCallback(
    (e) => setTicketCount(Math.max(1, parseInt(e.target.value)) || 0),
    []
  );

  const buyRaffleTickets = useCallback(
    async (ticketCount) => {
      if (rainbows) {
        const amount = ticketPrice.mul(ticketCount);

        const gasLimit = await rainbows.estimateGas.transfer(
          raffleAccount,
          amount
        );

        const feeData = await library.getFeeData();

        rainbows
          .transfer(raffleAccount, amount, { gasLimit, ...feeData }) // workaround for metamask mobile, will crash on desktop
          .catch((e) => {
            if (e.code != 4001) return rainbows.transfer(raffleAccount, amount);
          })
          .then((tx) => {
            setBuying(true);
            return tx.wait();
          })
          .catch((e) => console.error(e))
          .finally(() => setBuying(false));
      }
    },
    [rainbows, ticketPrice, library]
  );

  const maxTickets = rainbowBalance.current.div(ticketPrice).toNumber();
  const disableBuy = ticketCount < 1 || ticketCount > maxTickets || buying;

  const myTicketCount = _.get(participants, account, 0);
  const totalTicketCount = _.sum(Object.values(participants));

  if (!raffleStarted) {
    return (
      <div className="py-5 text-center">
        <p className="lead">
          Raffle will start in{' '}
          <strong>
            <Countdown
              date={raffleStart}
              daysInHours
              onComplete={() => setRaffleStarted(true)}
            />
          </strong>
        </p>
      </div>
    );
  }

  return (
    <>
      {raffleEnded ? (
        <div className="alert alert-dark text-center lead">
          This raffle has ended
        </div>
      ) : (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="h2">Raffle tickets</Form.Label>
            <Form.Control
              type="number"
              placeholder="number of tickets"
              min={1}
              value={ticketCount.toString()}
              onChange={updateTicketCount}
            />
            <Form.Text className="text-muted">
              <strong>Cost per ticket:</strong> {formatEther(ticketPrice)}{' '}
              $RAINBOWS
            </Form.Text>
          </Form.Group>

          <p>
            <strong>Total:</strong> {formatEther(ticketPrice.mul(ticketCount))}{' '}
            $RAINBOWS
          </p>
          <Button
            onClick={() => buyRaffleTickets(ticketCount)}
            disabled={disableBuy}
            size="lg"
            className="btn-primary rounded-pill w-100"
          >
            Enter raffle
          </Button>
          <div className="mt-4">
            <RainbowBalance price={ticketPrice.mul(ticketCount)} />
          </div>
        </>
      )}
      <hr />
      <p>
        <strong>Your raffle tickets:</strong> {myTicketCount}
        <br />
        <strong>Total raffle tickets:</strong> {totalTicketCount}
      </p>
    </>
  );
};

export default RaffleEntryForm;
